'use client';

import React, { useEffect, useState } from "react";
import { Dialog, DialogPanel, DialogTitle } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { Link } from "react-router-dom";
import { buildCodeHierarchyRows, formatCodeCitation, getCodeBaseSection, toEasternLocaleDateString } from "../../utils";
import { apiFetch } from "../../api";
import LoadingSpinner from "../Common/LoadingSpinner";
import { getCachedCode, loadCodeList, setCachedCode } from "./codeLibraryData";

/**
 * Renders inline trigger text for a code that opens a right-side drawer with code details.
 * Provides a CTA to jump to the full code detail page.
 */
export default function CodeDrawerLink({
  codeId,
  children,
  className = "font-semibold text-blue-700 hover:underline",
  ...buttonProps
}) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [code, setCode] = useState(() => getCachedCode(codeId));
  const [error, setError] = useState(null);
  const [relatedRows, setRelatedRows] = useState([]);



  useEffect(() => {
    let cancelled = false;
    if (!codeId) return;

    // A row primed from the list response carries everything the drawer shows
    // (description, chapter, section, timestamps), so this usually avoids a fetch.
    const cached = getCachedCode(codeId);
    if (cached) {
      setCode(cached);
      return;
    }

    setLoading(true);
    setError(null);

    apiFetch(`/codes/${codeId}`)
      .then((response) => {
        if (!response.ok) {
          throw new Error("Unable to load code details");
        }
        return response.json();
      })
      .then((data) => {
        if (cancelled) return;
        setCachedCode(codeId, data);
        setCode(data);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message || "Unable to load code details");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [codeId]);

  const handleOpen = () => {
    if (!codeId) return;
    setOpen(true);
  };

  const heading = code ? formatCodeCitation(code) : "Code Details";

  const linkText = code ? `${formatCodeCitation(code)} (${code.name})` : children;

  useEffect(() => {
    let cancelled = false;
    if (!open || !code?.chapter || !code?.section) {
      setRelatedRows([]);
      return undefined;
    }

    loadCodeList()
      .then((codes) => {
        if (cancelled || !Array.isArray(codes)) return;
        const baseSection = getCodeBaseSection(code);
        const related = codes.filter(
          (candidate) => candidate.chapter === code.chapter && getCodeBaseSection(candidate) === baseSection
        );
        setRelatedRows(buildCodeHierarchyRows(related));
      })
      .catch(() => {
        if (!cancelled) setRelatedRows([]);
      });

    return () => {
      cancelled = true;
    };
  }, [open, code]);


  return (
    <>
      <button
        type="button"
        onClick={handleOpen}
        className={className}
        {...buttonProps}
      >
        {linkText}
      </button>

      <Dialog open={open} onClose={setOpen} className="relative z-[1300]">
        <div className="fixed inset-0 z-10 bg-gray-900/30 backdrop-blur-sm" aria-hidden="true" />
        <div className="fixed inset-0 z-20 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10 sm:pl-16">
              <DialogPanel className="pointer-events-auto w-screen max-w-md transform bg-white transition duration-500 ease-in-out data-[closed]:translate-x-full sm:duration-700">
                <div className="flex h-full flex-col overflow-y-auto bg-white shadow-xl">
                  <header className="px-4 pb-4 pt-6 sm:px-6">
                    <div className="flex items-start justify-between">
                      <div>
                        <DialogTitle className="text-base font-semibold text-gray-900">
                          {heading}
                        </DialogTitle>
                        {code?.name && (
                          <p className="mt-1 text-sm text-gray-600">{code.name}</p>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => setOpen(false)}
                        className="rounded-md text-gray-400 hover:text-gray-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                      >
                        <span className="sr-only">Close panel</span>
                        <XMarkIcon aria-hidden="true" className="h-6 w-6" />
                      </button>
                    </div>
                  </header>

                  <div className="flex-1 space-y-6 px-4 pb-6 sm:px-6">
                    {loading && (
                      <div className="flex justify-center py-4"><LoadingSpinner className="h-6 w-6 text-indigo-600" /></div>
                    )}
                    {error && (
                      <p className="text-sm text-red-600">{error}</p>
                    )}
                    {!loading && !error && code && (
                      <>
                        <section>
                          <h2 className="text-sm font-semibold text-gray-900">
                            Description
                          </h2>
                          <p className="mt-2 text-sm text-gray-700 whitespace-pre-line">
                            {code.description || "No description provided."}
                          </p>
                        </section>

                        {relatedRows.length > 1 && (
                          <section>
                            <h2 className="text-sm font-semibold text-gray-900">
                              Section Structure
                            </h2>
                            <div className="mt-2 space-y-1 text-sm">
                              {relatedRows.map(({ code: relatedCode, depth }) => {
                                const isCurrent = Number(relatedCode.id) === Number(code.id);
                                return (
                                  <Link
                                    key={relatedCode.id}
                                    to={`/code/${relatedCode.id}`}
                                    onClick={() => setOpen(false)}
                                    className={`block rounded px-2 py-1 ${isCurrent ? 'bg-indigo-50 font-semibold text-indigo-800' : 'text-gray-700 hover:bg-gray-50'}`}
                                    style={{ marginLeft: `${Math.min(depth, 4) * 1}rem` }}
                                  >
                                    {formatCodeCitation(relatedCode)}: {relatedCode.name}
                                  </Link>
                                );
                              })}
                            </div>
                          </section>
                        )}

                        <section className="grid grid-cols-1 gap-3 text-sm text-gray-600 sm:grid-cols-2">
                          <div>
                            <span className="font-medium text-gray-900">Chapter</span>
                            <p>
                              {code.chapter ? (
                                <Link to={`/codes?chapter=${code.chapter}`} className="text-indigo-600 hover:underline">
                                  {code.chapter}
                                </Link>
                              ) : "—"}
                            </p>
                          </div>
                          <div>
                            <span className="font-medium text-gray-900">Section</span>
                            <p>
                              <Link to={`/code/${codeId}`} className="text-indigo-600 hover:underline" onClick={() => setOpen(false)}>
                                {code.section || "—"}
                              </Link>
                            </p>
                          </div>
                          <div>
                            <span className="font-medium text-gray-900">Created</span>
                            <p>
                              {code.created_at
                                ? toEasternLocaleDateString(code.created_at) || "—"
                                : "—"}
                            </p>
                          </div>
                          <div>
                            <span className="font-medium text-gray-900">Updated</span>
                            <p>
                              {code.updated_at
                                ? toEasternLocaleDateString(code.updated_at) || "—"
                                : "—"}
                            </p>
                          </div>
                        </section>
                      </>
                    )}
                  </div>

                  <div className="border-t border-gray-200 px-4 py-4 sm:px-6">
                    <div className="flex justify-end gap-3">
                      <button
                        type="button"
                        onClick={() => setOpen(false)}
                        className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                      >
                        Close
                      </button>
                      <Link
                        to={`/code/${codeId}`}
                        className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700"
                        onClick={() => setOpen(false)}
                      >
                        View full code
                      </Link>
                    </div>
                  </div>
                </div>
              </DialogPanel>
            </div>
          </div>
        </div>
      </Dialog>
    </>
  );
}
